import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { X, Calendar, Clock, MapPin, BookOpen, Users, RefreshCw, PlayCircle, Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import api from '../../api';

/**
 * REDESIGNED UPCOMING CLASSES MODAL
 * Focus: Mobile-First, Premium UI/UX, Dynamic Layouts
 */

const useLiveClassTimers = (classItems) => {
  const [liveClasses, setLiveClasses] = useState([]);

  const calculateLiveClassStatus = useCallback((classItem, currentTime, isUpdate = false) => {
    const isCompleted = Boolean(
      classItem.hasAttendanceMarked ||
      classItem.attendanceMarked ||
      classItem.timeStatus === 'completed' ||
      classItem.status === 'completed' ||
      classItem.status === 'done' ||
      classItem.attendanceStatus === 'marked' ||
      classItem.isCompleted ||
      classItem.completed === true
    );

    const fallbackClassTime = classItem.classTime || (
      classItem.sessionDate && classItem.startTime
        ? moment(`${moment(classItem.sessionDate).format('YYYY-MM-DD')} ${classItem.startTime}`, 'YYYY-MM-DD HH:mm').toISOString()
        : null
    );
    const classTime = fallbackClassTime ? moment(fallbackClassTime) : null;
    const timeDiff = classTime ? classTime.diff(currentTime, 'minutes') : 0;
    
    let timeStatus = 'upcoming';
    let timeUntil = '';
    let isLive = false;

    if (isCompleted) {
      timeStatus = 'completed';
      timeUntil = 'Completed';
      return {
        ...classItem,
        timeStatus,
        timeUntil,
        minutesUntil: timeDiff,
        isLive: false,
        lastUpdated: currentTime.valueOf()
      };
    }
    
    if (classTime && timeDiff <= 0 && timeDiff >= -120) {
      timeStatus = 'in_progress';
      const absMins = Math.abs(Math.floor(timeDiff));
      timeUntil = absMins === 0 ? 'Starting now' : `Started ${absMins}m ago`;
      isLive = true;
    } else if (classTime && timeDiff <= 30 && timeDiff > 0) {
      timeStatus = 'starting_soon';
      timeUntil = `In ${Math.floor(timeDiff)}m`;
    } else if (classTime && timeDiff >= 60) {
      timeStatus = 'upcoming';
      const hours = Math.floor(timeDiff / 60);
      const mins = Math.floor(timeDiff % 60);
      timeUntil = mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else if (classTime && timeDiff > 0) {
      timeStatus = 'upcoming';
      timeUntil = `${Math.floor(timeDiff)}m`;
    } else {
      timeStatus = 'ended';
      timeUntil = 'Done';
    }

    return {
      ...classItem,
      timeStatus,
      timeUntil,
      minutesUntil: timeDiff,
      isLive,
      lastUpdated: currentTime.valueOf()
    };
  }, []);

  useEffect(() => {
    if (!classItems.length) {
      setLiveClasses([]);
      return;
    }

    const updateAll = () => {
      const now = moment();
      setLiveClasses(classItems.map(item => calculateLiveClassStatus(item, now)));
    };

    updateAll();
    const interval = setInterval(updateAll, 30000);
    return () => clearInterval(interval);
  }, [classItems, calculateLiveClassStatus]);

  return liveClasses;
};

const UpcomingClassesModal = ({ isOpen, onClose, facultyId }) => {
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const liveClasses = useLiveClassTimers(upcomingClasses);

  const buildScheduleRoomKey = useCallback((sessionDate, startTime, endTime, classRef, subjectRef) => {
    const normalizeTime = (value) => {
      if (!value) return '';
      const parsed = moment(value, ['HH:mm', 'H:mm', 'hh:mm A', 'h:mm A'], true);
      return parsed.isValid() ? parsed.format('HH:mm') : String(value).trim();
    };

    return [
      sessionDate ? moment(sessionDate).format('YYYY-MM-DD') : '',
      normalizeTime(startTime),
      normalizeTime(endTime),
      classRef || '',
      subjectRef || ''
    ].join('|');
  }, []);

  const mergeScheduleRoomData = useCallback((attendanceSchedule, timetableClasses) => {
    const roomMap = new Map();

    const timetableEntries = (timetableClasses || []).flatMap(day => {
      const dayDateISO = day?.dateISO || day?.date || null;
      if (Array.isArray(day?.entries)) {
        return day.entries.map(entry => ({ ...entry, _dayDateISO: dayDateISO }));
      }
      if (Array.isArray(day)) return day;
      return [];
    });

    timetableEntries.forEach(item => {
      const classRef = item.classId?._id || item.classId || item.className || item.classCode;
      const subjectRef = item.subjectId?._id || item.subjectId || item.subjectName || item.subjectCode;
      const key = buildScheduleRoomKey(
        item.sessionDate || item._dayDateISO || item.date,
        item.startTime || item.timeSlot?.start || item.timeSlot?.originalStart,
        item.endTime || item.timeSlot?.end || item.timeSlot?.originalEnd,
        classRef,
        subjectRef
      );
      if (key) roomMap.set(key, item.room || item.roomName || item.classRoom || item.classroom || item.location?.name || '');
    });

    return (attendanceSchedule || []).map(item => {
      const classRef = item.classId?._id || item.classId || item.className || item.classCode;
      const subjectRef = item.subjectId?._id || item.subjectId || item.subjectSnapshot?.subjectName || item.subjectId?.name || item.subjectCode;
      const key = buildScheduleRoomKey(
        item.sessionDate,
        item.startTime || item.timeSlot?.start,
        item.endTime || item.timeSlot?.end,
        classRef,
        subjectRef
      );
      return {
        ...item,
        room: item.room || roomMap.get(key) || item.roomName || item.classRoom || item.classroom || ''
      };
    });
  }, [buildScheduleRoomKey]);

  const fetchUpcomingClasses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [attendanceResponse, timetableResponse] = await Promise.all([
        api.get(`/api/faculty/${encodeURIComponent(facultyId)}/attendance/today-schedule`),
        api.get(`/api/faculty/${encodeURIComponent(facultyId)}/timetable/personal`, {
          params: {
            weekStartDate: new Date().toISOString().split('T')[0],
            viewMode: 'custom'
          }
        })
      ]);

      if (attendanceResponse.data.success) {
        const backendData = attendanceResponse.data.data?.schedule || [];
        const timetableData = timetableResponse.data?.success ? timetableResponse.data.data?.week || [] : [];
        setUpcomingClasses(mergeScheduleRoomData(backendData, timetableData));
      } else {
        throw new Error('Could not sync schedule');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [facultyId, mergeScheduleRoomData]);

  useEffect(() => {
    if (isOpen) fetchUpcomingClasses();
  }, [isOpen, fetchUpcomingClasses]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-md transition-all">
      {/* Container: Full screen on mobile, Rounded modal on desktop */}
      <div className="relative w-full max-w-lg h-[92vh] sm:h-auto sm:max-h-[85vh] bg-slate-50 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-300">
        
        {/* Handle for Mobile UX */}
        <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto mt-3 mb-1 sm:hidden" />

        {/* Header */}
        <header className="px-5 py-4 flex items-center justify-between bg-white border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-100">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-slate-800 leading-tight">My Schedule</h2>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{moment().format('dddd, MMM DD')}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button 
              onClick={fetchUpcomingClasses}
              className="p-2 text-slate-500 hover:bg-slate-50 rounded-full active:scale-90 transition-all"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-slate-500 hover:bg-slate-50 rounded-full active:scale-90 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Scrollable Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
              <p className="font-medium">Syncing your sessions...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10 px-6">
              <div className="inline-flex p-4 bg-red-50 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-slate-800 font-bold text-lg">Connection Issue</h3>
              <p className="text-slate-500 text-sm mb-6">{error}</p>
              <button 
                onClick={fetchUpcomingClasses}
                className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold shadow-lg"
              >
                Try Again
              </button>
            </div>
          ) : liveClasses.length > 0 ? (
            <div className="space-y-4">
              {liveClasses.map((item, idx) => (
                <SessionCard key={idx} session={item} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 px-6">
              <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-indigo-200" />
              </div>
              <h3 className="text-slate-800 font-bold text-lg">No more classes!</h3>
              <p className="text-slate-500 text-sm">Your schedule is clear for the rest of the day.</p>
            </div>
          )}
        </div>

        {/* Modern Bottom Bar */}
        <footer className="p-4 bg-white border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Auto-sync Active</span>
          </div>
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-slate-100 text-slate-700 font-bold text-sm rounded-xl hover:bg-slate-200 transition-colors"
          >
            Dismiss
          </button>
        </footer>
      </div>
    </div>
  );
};

/* Sub-component for better performance and readability */
const SessionCard = ({ session }) => {
  const isLive = session.timeStatus === 'in_progress';
  const isSoon = session.timeStatus === 'starting_soon';
  const isEnded = session.timeStatus === 'ended';
  const isCompleted = session.timeStatus === 'completed';

  const statusStyles = {
    in_progress: "bg-red-500 ring-4 ring-red-50 text-white",
    starting_soon: "bg-amber-500 ring-4 ring-amber-50 text-white",
    upcoming: "bg-emerald-500 text-white",
    ended: "bg-slate-200 text-slate-500",
    completed: "bg-emerald-600 text-white"
  };

  return (
    <div className={`group relative bg-white border rounded-2xl p-4 transition-all duration-300 active:scale-[0.98] ${
      isLive ? 'border-red-200 shadow-md ring-1 ring-red-100' : 
      isEnded ? 'opacity-60 grayscale-[0.5] border-slate-100' : 'border-slate-200 shadow-sm'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter flex items-center gap-1.5 ${statusStyles[session.timeStatus] || statusStyles.upcoming}`}>
          {isLive && <PlayCircle className="w-3 h-3 animate-pulse" />}
          {isSoon && <Clock className="w-3 h-3" />}
          {isCompleted ? 'completed' : session.timeStatus.replace('_', ' ')}
        </div>
        <span className="text-xs font-bold text-slate-400">
          {session.timeUntil}
        </span>
      </div>

      <div className="flex gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
          session.type === 'Lab' ? 'bg-purple-50 text-purple-600' : 
          session.type === 'Elective' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
        }`}>
          {session.type === 'Lab' ? <Users className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-bold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
            {session.subjectName || session.subjectSnapshot?.subjectName || session.subjectId?.name || 'Unscheduled Session'}
          </h4>
          <div className="flex items-center gap-2 mt-0.5 text-slate-500 text-xs font-medium">
            <span className="bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wide text-[10px]">{session.className || session.classId?.className || 'Unknown Class'}</span>
            {session.batchName && <span className="text-indigo-500">Batch {session.batchName}</span>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 self-center" />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-bold">{session.timeSlot?.start || session.startTime || ''}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <MapPin className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-bold truncate max-w-[80px]">{session.room || session.roomName || 'Not Alloted'}</span>
          </div>
        </div>
        <div className="text-[10px] text-slate-300 italic">
          {session.timeUntil || ''}
        </div>
      </div>
    </div>
  );
};

export default UpcomingClassesModal;